import { FC, KeyboardEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';

import { Icon } from '@purpur/library/icon';
import { chevronLeft } from '@purpur/library/icon/assets/chevron-left';
import { close } from '@purpur/library/icon/assets/close';
import { search } from '@purpur/library/icon/assets/search';
import _ from 'lodash';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserModalControls } from '@telialt/common-hooks/src/useBrowserModalControls';
import { useFocusLost } from '@telialt/common-outside-click/src';
import { useComponentId } from '@telialt/utils/src/hooks';

import { useEscapeClose } from '../../hooks/useEscapeClose';
import { SEARCH_TEST_ID } from '../../test/test-ids';
import { LoadingSpinner } from '../SearchInput/LoadingSpinner';
import {
    ClearButton,
    CloseButton,
    InputWrapper,
    MobileCloseButton,
    OpenButton,
    SearchButton,
    SearchInput,
} from './NavBarSearch.styles';
import { CustomComponents } from './NavBarSearchConnected';
import { NavBarSearchDropdown } from './NavBarSearchDropdown';
import { NavBarWrapper } from './NavBarWrapper';

const DEFAULT_MIN_LENGTH = 3;
const DEFAULT_DEBOUNCE_TIME = 300;

const isElementVisible = (elem?: HTMLElement) =>
    !elem || elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length;

type Props = {
    minLength?: number;
    debounceTime?: number;
    placeholder?: string;
    onChange: (keyword: string) => void;
    onSubmit: (keyword: string) => void;
    onFocus: (keyword: string) => void;
    isLoading?: boolean;
    content?: ReactNode;
    components?: CustomComponents;
    navigationBarHeight?: number;
};

export const NavBarSearch: FC<Props> = ({
    minLength = DEFAULT_MIN_LENGTH,
    debounceTime = DEFAULT_DEBOUNCE_TIME,
    placeholder,
    onChange,
    onSubmit,
    onFocus,
    isLoading,
    content,
    components,
    navigationBarHeight,
}) => {
    const { t } = useTranslations();

    const inputRef = useRef<HTMLInputElement>(null);
    const openButtonRef = useRef<HTMLButtonElement>(null);
    const [rerenderTrigger, setRerenderTrigger] = useState(0);

    const id = useComponentId();

    const { isModalOpen: isActive, closeModal, openModal } = useBrowserModalControls('search-input-dropdown' + id);

    useEffect(() => {
        const handleResize = () => {
            if (isActive && !isElementVisible(inputRef.current)) {
                closeModal();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [closeModal, isActive]);

    useEffect(() => {
        if (isActive) {
            inputRef.current?.focus();
        }
    }, [isActive]);

    const handleSearchOpen = (event: MouseEvent) => {
        event.stopPropagation();
        if (!isActive) {
            openModal();
        }
    };

    const handleDropdownClose = () => {
        if (isActive) {
            closeModal();
            onChange?.('');
            setTimeout(() => {
                // Force rerender, this fixes cached DOM issue in NVDA screen reader
                setRerenderTrigger((prev) => prev + 1);
                // Restore focus to the open button after closing the dropdown
                setTimeout(() => openButtonRef.current?.focus(), 10);
            }, 0);
        }
        if (!inputRef.current) {
            return;
        }
        inputRef.current.blur();
    };

    const [setWrapperRef] = useFocusLost(() => handleDropdownClose());

    useEscapeClose(handleDropdownClose);

    const handleFocus = () => {
        if (inputRef.current?.value.length >= minLength) {
            onFocus?.(inputRef.current?.value.trim());
        }
    };

    const handleInputDebounced = _.debounce((inputValue: string) => {
        if (inputValue.length >= minLength) {
            onChange?.(inputValue.trim());
        }
    }, debounceTime);

    const handleInputChange = () => {
        handleInputDebounced(inputRef.current?.value);
    };

    const handleInputClear = () => {
        if (!inputRef.current) {
            return;
        }
        inputRef.current.value = '';
        onChange?.('');
        inputRef.current.focus();
    };

    const handleSubmit = () => {
        onSubmit?.(inputRef.current?.value.trim());
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            handleSubmit();
        }
    };

    const ID = {
        SEARCH_BOX: `navbar-search-box${id}`,
        SEARCH_INPUT_DESCRIPTION: `navbar-search-box-description${id}`,
    };

    const openButtonProps = {
        onClick: handleSearchOpen,
        ref: openButtonRef,
        'aria-label': t['navbar.search.aria_label'],
        'data-test': SEARCH_TEST_ID.NAVBAR_SEARCH_INPUT_OPEN_BUTTON,
        'aria-controls': ID.SEARCH_BOX,
        'aria-expanded': isActive,
    };

    const closeButtonProps = {
        children: t['search.component.navbar_search.close_button'],
        onClick: handleDropdownClose,
        'aria-label': t['navbar.search.close_btn.aria_label'],
        'data-test': SEARCH_TEST_ID.NAVBAR_SEARCH_DESKTOP_INPUT_CLOSE_BUTTON,
    };

    return (
        <div data-test={SEARCH_TEST_ID.NAVBAR_SEARCH} key={rerenderTrigger}>
            <span hidden={true} id={ID.SEARCH_INPUT_DESCRIPTION}>
                {t['navbar.search.input_field.aria_label']}
            </span>
            {components?.openButton?.(openButtonProps) || (
                <OpenButton variant="text" {...openButtonProps}>
                    <Icon svg={search} size="lg" />
                </OpenButton>
            )}
            {isActive && (
                <NavBarWrapper
                    ref={setWrapperRef}
                    navigationBarHeight={navigationBarHeight}
                    id={ID.SEARCH_BOX}
                    data-test={SEARCH_TEST_ID.NAVBAR_SEARCH_WIDGET}
                >
                    <InputWrapper>
                        <SearchInput
                            data-test={SEARCH_TEST_ID.NAVBAR_SEARCH_INPUT_INPUT}
                            ref={inputRef}
                            autoComplete="off"
                            onFocus={handleFocus}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            maxLength={255}
                            $isLoading={isLoading}
                            aria-describedby={ID.SEARCH_INPUT_DESCRIPTION}
                        />
                        <LoadingSpinner isLoading={isLoading} isInputActive={isActive} />
                        <SearchButton
                            data-test={SEARCH_TEST_ID.NAVBAR_SEARCH_INPUT_SEARCH_BUTTON}
                            onClick={handleSubmit}
                            aria-label={t['navbar.search.start_btn.aria_label']}
                        >
                            <Icon svg={search} size="md" />
                        </SearchButton>
                        <MobileCloseButton
                            onClick={handleDropdownClose}
                            data-test={SEARCH_TEST_ID.NAVBAR_SEARCH_MOBILE_INPUT_CLOSE_BUTTON}
                            aria-label={t['navbar.search.close_btn.aria_label']}
                        >
                            <Icon svg={chevronLeft} size="md" />
                        </MobileCloseButton>
                        <ClearButton
                            data-test={SEARCH_TEST_ID.NAVBAR_SEARCH_INPUT_CLEAR_BUTTON}
                            onClick={handleInputClear}
                            aria-label={t['navbar.search.clear_btn.aria_label']}
                        >
                            <Icon svg={close} size="md" />
                        </ClearButton>
                    </InputWrapper>

                    {components?.closeButton?.(closeButtonProps) || <CloseButton {...closeButtonProps} />}

                    {content && (
                        <NavBarSearchDropdown
                            isActive={isActive}
                            onOverlayClick={handleDropdownClose}
                            content={content}
                            navigationBarHeight={navigationBarHeight}
                        />
                    )}
                </NavBarWrapper>
            )}
        </div>
    );
};
